// src/pages/Headquarter/EditHeadquarter.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import headquarterService from '../../services/headquarter.service';
import regionService from '../../services/region.service';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const EditHeadquarter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [regions, setRegions]           = useState([]);
  const [toast, setToast]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors]             = useState({});

  const [formData, setFormData] = useState({
    name:     '',
    regionId: '',
  });

  useEffect(() => {
    const init = async () => {
      try {
        const companyId = isSuperAdmin
          ? localStorage.getItem('selectedCompanyId')
          : user?.companyId;

        const [hqRes, regionsRes] = await Promise.all([
          headquarterService.getHeadquarterById(id),
          regionService.getAllRegions(companyId),
        ]);

        if (hqRes.success) {
          const h = hqRes.data;
          setFormData({
            name:     h.name     || '',
            regionId: h.regionId ? String(h.regionId) : '',
          });
        }

        if (regionsRes.success && regionsRes.data) {
          setRegions(regionsRes.data);
        }
      } catch (err) {
        setToast({ message: 'Failed to load headquarter data', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name     = 'Headquarter name is required';
    if (!formData.regionId)    e.regionId = 'Region is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name:     formData.name.trim(),
        regionId: parseInt(formData.regionId, 10),
      };

      const res = await headquarterService.updateHeadquarter(id, payload);
      if (res.success) {
        setToast({ message: 'Headquarter updated successfully!', type: 'success' });
        setTimeout(() => navigate('/headquarter/view'), 1200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update headquarter',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="add-headquarter-page">
        <p style={{ padding: 40 }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="add-headquarter-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/headquarter/view">Headquarters</a></li>
          <li className="breadcrumb-item active">Edit Headquarter</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Headquarter</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Headquarter Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter headquarter name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Region <span className="required">*</span>
                </label>
                <select
                  name="regionId"
                  className={`form-input ${errors.regionId ? 'error' : ''}`}
                  value={formData.regionId}
                  onChange={handleChange}
                >
                  <option value="">Select Region</option>
                  {regions.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                {errors.regionId && <span className="error-message">{errors.regionId}</span>}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/headquarter/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditHeadquarter;